import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const highlightColors = {
  trip_purpose: "rgba(245, 158, 11, 0.4)",
  presentation: "rgba(14, 165, 233, 0.4)",
  delivery: "rgba(16, 185, 129, 0.4)",
  next_steps: "rgba(244, 63, 94, 0.4)",
} as const;

type HighlightCategory = keyof typeof highlightColors;
type HighlightedLines = Record<HighlightCategory, number[]>;
type MarkdownNode = {
  type: string;
  value?: string;
  children?: MarkdownNode[];
  position?: { start: { line: number }; end: { line: number } };
  data?: { hName?: string; hProperties?: Record<string, string> };
};

function remarkHighlightLines(highlightedLines: HighlightedLines) {
  const categoryForLine = (line: number) => (
    (Object.keys(highlightColors) as HighlightCategory[]).find((category) => highlightedLines[category].includes(line))
  );

  return (tree: MarkdownNode) => {
    const highlightTextNodes = (node: MarkdownNode) => {
      if (!node.children) return;

      node.children = node.children.flatMap((child) => {
        if (child.type !== "text" || child.value === undefined || !child.position) {
          highlightTextNodes(child);
          return [child];
        }

        let line = child.position.start.line;
        return child.value.split(/(\n)/).map((value) => {
          const category = categoryForLine(line);
          const textNode = category && value !== "\n"
            ? {
                ...child,
                value,
                data: {
                  ...child.data,
                  hName: "span",
                  hProperties: { "data-instruction-highlight": category },
                },
              }
            : { ...child, value };

          if (value === "\n") line += 1;
          return textNode;
        });
      });
    };

    highlightTextNodes(tree);
  };
}

function AdminMarkdownPreview({ markdown, highlightedLines }: { markdown: string; highlightedLines: HighlightedLines }) {
  const [tab, setTab] = useState<"source" | "rendered">("source");
  const sourceLines = markdown.split("\n");
  const categoryForLine = (line: number) => (
    (Object.keys(highlightColors) as HighlightCategory[]).find((category) => highlightedLines[category].includes(line))
  );

  return (
    <section className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/50">
      <div className="flex gap-1 border-b border-zinc-800 bg-zinc-950/70 p-2" role="tablist" aria-label="Instruction preview format">
        {(["source", "rendered"] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            className={`rounded-md px-3 py-2 text-sm font-medium capitalize transition ${tab === value ? "bg-zinc-800 text-white" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"}`}
            onClick={() => setTab(value)}
          >
            {value}
          </button>
        ))}
      </div>

      {tab === "source" ? (
        <pre role="tabpanel" className="max-h-[75vh] overflow-auto whitespace-pre-wrap p-5 font-mono text-xs leading-6 text-zinc-200">
          {sourceLines.map((line, index) => {
            const category = categoryForLine(index + 1);
            return (
              <React.Fragment key={index}>
                <span className={category ? "box-decoration-clone rounded-sm" : undefined} style={category ? { backgroundColor: highlightColors[category] } : undefined}>{line}</span>
                {index < sourceLines.length - 1 && "\n"}
              </React.Fragment>
            );
          })}
        </pre>
      ) : (
        <article role="tabpanel" className="max-h-[75vh] overflow-auto p-6 text-sm leading-7 text-zinc-200">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, [remarkHighlightLines, highlightedLines]]}
            skipHtml
            components={{
              h1: (props) => <h1 className="mb-4 mt-8 text-2xl font-bold text-white first:mt-0" {...props} />,
              h2: (props) => <h2 className="mb-3 mt-7 text-xl font-semibold text-white" {...props} />,
              h3: (props) => <h3 className="mb-2 mt-6 text-base font-semibold text-zinc-100" {...props} />,
              p: (props) => <p className="mb-4" {...props} />,
              ul: (props) => <ul className="mb-4 list-disc space-y-1 pl-6" {...props} />,
              ol: (props) => <ol className="mb-4 list-decimal space-y-1 pl-6" {...props} />,
              span: ({ node, ...props }) => {
                const category = props["data-instruction-highlight"] as HighlightCategory | undefined;
                return <span className={category ? "box-decoration-clone rounded-sm" : undefined} style={category ? { backgroundColor: highlightColors[category] } : undefined} {...props} />;
              },
              a: ({ href, ...props }) => <a href={href} onClick={(event) => event.preventDefault()} className="text-sky-300 underline decoration-sky-500/50 underline-offset-2" {...props} />,
              code: (props) => <code className="rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-xs text-amber-200" {...props} />,
              blockquote: (props) => <blockquote className="mb-4 border-l-2 border-violet-400 pl-4 text-zinc-400" {...props} />,
            }}
          >
            {markdown}
          </ReactMarkdown>
        </article>
      )}
    </section>
  );
}

document.querySelectorAll<HTMLElement>("[data-admin-markdown-preview]").forEach((element) => {
  const payload = element.querySelector<HTMLScriptElement>("[data-admin-markdown-preview-payload]");
  if (!payload?.textContent) return;

  const { markdown, highlightedLines } = JSON.parse(payload.textContent);
  createRoot(element).render(<AdminMarkdownPreview markdown={markdown} highlightedLines={highlightedLines} />);
});
